import { reactive, computed, unref, ref, watch } from "vue"
import { useAuth0 } from "@auth0/auth0-vue"
import axios from "axios"
import { chargeBeeEditBilling, chargeBeeEditPayment } from "@/chargebee.js"
import { toast } from "@/toast/index.js"

const normalize = (auth0User, authorizationUser) => {
  const unwrappedAuth0User = unref(auth0User)
  const unwrappedAuthorizationUser = unref(authorizationUser) || {}

  const keyEndsWith = s => Object.keys(unwrappedAuth0User).find(key => key.endsWith(s))
  const identity = unwrappedAuth0User[keyEndsWith("identities")]
  const useMfa = unwrappedAuth0User[keyEndsWith("use_mfa")]
  const forceMfa = unwrappedAuth0User[keyEndsWith("force_mfa")]
  const userWithoutCustomClaims = Object.keys(unwrappedAuth0User)
    .filter(key => !key.startsWith("http"))
    .reduce((obj, key) => {
      obj[key] = unwrappedAuth0User[key]
      return obj
    }, {})
  return {
    ...userWithoutCustomClaims,
    role: unwrappedAuthorizationUser.role,
    identity: identity ? identity[0] : {},
    useMfa,
    forceMfa,
  }
}

const cachedAuthorizationUser = () => {
  let user = {}
  const wrapper = async () => {
    if (Object.keys(user).length > 0) {
      return user
    }
    else {
      user = await fetchAuthorizationUser()
      return user
    }
  }

  return {
    fetchAuthorizationUser: wrapper,
  }
}

const fetchAuthorizationUser = async () => {
  try {
    const { data } = await axios.get("/authorization/user")
    return data.user
  }
  catch (e) {
    toast.error(e)
    return {}
  }
}

const useAccount = () => {
  const loading = reactive({
    reset: false,
    billing: false,
    payment: false,
    mfa: false,
  })

  const { isAuthenticated, user: auth0User, logout: _logout } = useAuth0()
  const normalizedUser = computed(() => normalize(auth0User, authorizationUser))
  const authorizationUser = ref({})

  watch(isAuthenticated, async (val) => {
    if (val) {
      fetchAuthorizationUser().then((data) => {
        authorizationUser.value = data
      })
    }
  },
  { immediate: true },
  )

  const resetPassword = async () => {
    try {
      loading.reset = true
      await axios.post("/customer/reset_password")
      toast.success(
        `We sent you an email to ${normalizedUser.value.email} so you can reset your password.`,
      )
    }
    catch (e) {
      toast.error(e)
    }
    finally {
      loading.reset = false
    }
  }

  const logout = () => {
    return _logout({
      logoutParams: {
        returnTo: `${window.location.origin}/`,
      },
    })
  }

  const changeMfa = async (val) => {
    try {
      loading.mfa = true
      await axios.put("/customer/mfa", { mfa_flag: val })
      if (val) {
        // val === true will cause a logout because auth0 requires a new authentication
        await logout()
      }
      toast.success(`Multifactor authentication is now ${val ? "enabled" : "disabled"}.`)
    }
    catch (e) {
      toast.error(e)
    }
    finally {
      loading.mfa = false
    }
  }

  const editBilling = async () => {
    try {
      loading.billing = true
      await chargeBeeEditBilling()
    }
    // eslint-disable-next-line no-unused-vars
    catch (e) {
      toast.error("Sorry, we could not get your billing information.")
    }
    finally {
      loading.billing = false
    }
  }

  const editPayment = async () => {
    try {
      loading.payment = true
      await chargeBeeEditPayment()
    }
    // eslint-disable-next-line no-unused-vars
    catch (e) {
      toast.error("Sorry, we could not get your payment information.")
    }
    finally {
      loading.payment = false
    }
  }

  return {
    isAuthenticated,
    user: normalizedUser,
    loading,
    resetPassword,
    changeMfa,
    editBilling,
    editPayment,
    logout,
  }
}

export {
  cachedAuthorizationUser,
  useAccount,
  fetchAuthorizationUser,
}
